import {
    ActionButton,
    Button,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Tooltip,
} from "@jhool-io/fe-components";
import * as React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useFetchUserDetails } from "../../hooks/queries/user";
import NotificationBox from "../NotificationBox/NotificationBox";
import styles from "./Navbar.module.scss";
import {
    cn,
    convertArrayToString,
    formatDate,
    removeEnumUnderscore,
    userInitials,
} from "../../utils/helpers";
import { NotificationContext } from "../../context/notification/NotificationContext";
import { useFetchSessionNote } from "../../hooks/queries/note";
import SendIcon from "../Icons/Send";
import SearchClients from "../../modules/clients/components/SearchClients/SearchClients";
import useAnnouncement from "../../hooks/useAnnouncement";
import { useDoesUserHaveRole } from "../../hooks/permissions";
import moment from "../../utils/moment";

import { useAppDispatch, useAppSelector } from "../../hooks/useRedux";
import { toggleNavState } from "../../redux/features/nav/navSlice";
import Logo from "../../shared-ui/Logo/Logo";
import useAuthContext from "../../hooks/useAuthContext";
import { UserRole } from "../../utils/types/user";
import MenuIcon from "../../icons/Menu";
import InfoIcon from "../../icons/Info";
import { NoteStatus } from "../../modules/notes/types/notes.types";

interface NavProps {
    title?: string;
    titleTooltip?: string;
    showBackIconInNav?: boolean;
}

export default function Navbar({
    title,
    titleTooltip,
    showBackIconInNav = false,
}: NavProps) {
    const location = useLocation();

    const { isOpen } = useAppSelector((state) => state.nav);

    const { practice } = useAppSelector((state) => state.userPractice);

    const currentThemeFromLocalStorage = localStorage.getItem("theme") ?? "";

    const [currentTheme, setCurrentTheme] = React.useState(
        currentThemeFromLocalStorage
    );

    const { logOutUser } = useAuthContext();

    const dispatch = useAppDispatch();

    // Get id from url params
    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const { announcements, handleClearedAtValue, clearedBannerAt } =
        useAnnouncement();

    const noteContent = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    // Fetch user details
    const { data: loggedInUserData, isSuccess } = useFetchUserDetails();

    const userNotifications = React.useContext(NotificationContext);

    const isEditView = location.pathname.includes("edit");

    const isStaging = import.meta.env.VITE_NODE_ENV === "staging";

    /**
     * Determine whether to show the icon used to toggle between
     * edit and view mode
     */

    const shouldShowScreenIcon =
        noteContent.data?.data &&
        noteContent?.data.data.note_status === NoteStatus.DRAFT &&
        Boolean(clientId && noteId && location.pathname.includes("notes"));

    // Checks to decide if app notification banner should be shown
    const hasAnnouncements = announcements && announcements.length > 0;
    const practiceUsersCanSeeAnnouncement =
        (hasAnnouncements &&
            announcements[0]?.practice_ids.includes(
                practice?.practice_id || ""
            )) ||
        (hasAnnouncements && announcements[0]?.practice_ids.includes("all"));
    const isUserRoleInRolesAllowed = useDoesUserHaveRole(
        hasAnnouncements ? announcements?.[0]?.roles : []
    );
    const userRoleIsAllowedToView =
        (hasAnnouncements && isUserRoleInRolesAllowed) ||
        (hasAnnouncements &&
            announcements[0]?.roles.includes(
                "allusers" as unknown as UserRole
            ));

    const isAnnouncementDateGreaterThanClearedDate = () => {
        if (hasAnnouncements) {
            const clearedDate = clearedBannerAt
                ? moment(clearedBannerAt).toDate()
                : moment("2000-12-12").toDate();
            const announcementDate = new Date(
                formatDate(
                    announcements[0].created_at,
                    false,
                    "yyyy-MM-dd'T'HH:mm:ss"
                )
            );

            return announcementDate > clearedDate;
        }
        return false;
    };

    const showAnnouncement =
        announcements &&
        userRoleIsAllowedToView &&
        hasAnnouncements &&
        isAnnouncementDateGreaterThanClearedDate() &&
        practiceUsersCanSeeAnnouncement;

    const announcementContent = showAnnouncement ? (
        <>
            {formatDate(announcements[0].created_at, false, "MMM dd, yyyy")}
            <span className="mx-1">-</span>
            {announcements[0].details}
        </>
    ) : null;

    const handleThemeChange = (theme: "light" | "h-green") => {
        const htmlElement = document.querySelector("html");
        if (htmlElement) {
            htmlElement.dataset.theme = theme;
            localStorage.setItem("theme", theme);
            setCurrentTheme(theme);
        }
    };

    return (
        <div className="h-auto">
            {showAnnouncement && (
                <div className="w-full h-auto py-1 px-[--padding-card-x]  text-[#00563E] bg-[#ccfae97f] text-caption font-semibold flex items-center justify-center relative gap-x-2">
                    {announcements[0].has_redirect ? (
                        <Link
                            to={announcements[0].redirect_url || ""}
                            className="flex items-center gap-x-1 hover:gap-x-2 underline w-[95%] text-center"
                        >
                            {announcementContent}

                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="size-4 shrink-0"
                            >
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                        </Link>
                    ) : (
                        <span className="w-[95%] text-center">
                            {announcementContent}
                        </span>
                    )}

                    <Button
                        size="auto"
                        variant="normal"
                        className="absolute right-[20px]"
                        onClick={() => handleClearedAtValue?.()}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="size-4 shrink-0"
                        >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </Button>
                </div>
            )}
            <nav className="w-full flex justify-between h-10 px-[--padding-page] items-center [border-bottom:1px_solid_var(--color-stroke)]">
                {!isOpen && (
                    <div className="flex items-center gap-x-2">
                        <Logo />
                        <Button
                            size="auto"
                            variant="can-hover"
                            onClick={() => dispatch(toggleNavState())}
                        >
                            <MenuIcon className="stroke-textSec" />
                        </Button>
                    </div>
                )}
                <div
                    className={cn("flex items-center gap-x-2", {
                        hidden: !isOpen,
                    })}
                >
                    <h1 className="text-base font-semibold">{title}</h1>
                    {titleTooltip && (
                        <Tooltip
                            content={titleTooltip}
                            classNames="max-w-[320px] py-1"
                        >
                            <Button
                                size="auto"
                                variant="normal"
                                aria-label="About this page"
                            >
                                <InfoIcon />
                            </Button>
                        </Tooltip>
                    )}
                </div>

                <SearchClients />

                {isSuccess && loggedInUserData && (
                    <div className="flex items-center">
                        {shouldShowScreenIcon ? (
                            <Link
                                className={cn("mr-2 link", {
                                    "": isEditView,
                                })}
                                to={
                                    isEditView
                                        ? `/notes/${clientId}/${noteId}`
                                        : `/notes/${noteId}/${clientId}/edit`
                                }
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="size-4"
                                >
                                    <rect
                                        x="2"
                                        y="3"
                                        width="20"
                                        height="14"
                                        rx="2"
                                        ry="2"
                                    />
                                    <line x1="8" y1="21" x2="16" y2="21" />
                                    <line x1="12" y1="17" x2="12" y2="21" />
                                </svg>
                            </Link>
                        ) : null}
                        <a
                            href="https://docs.google.com/document/d/1BDxPqNJQbg4O8ZOuQ6mM4Q2yj-iUijIBXYC6-6XHjhc/edit?usp=sharing"
                            target="_blank"
                            rel="noreferrer"
                            className="text-base link mr-2"
                        >
                            <SendIcon /> FAQs
                        </a>
                        {!showBackIconInNav && (
                            <Popover>
                                <PopoverTrigger
                                    className={styles.notification}
                                    asChild
                                >
                                    <ActionButton
                                        variant="can-hover"
                                        size="auto"
                                        className="relative"
                                        actionTitle="View notifications"
                                    >
                                        <>
                                            <svg
                                                viewBox="0 0 24 24"
                                                width="24"
                                                height="24"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="size-4"
                                            >
                                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                                            </svg>
                                            {userNotifications.some(
                                                (n) => !n.is_read
                                            ) && (
                                                <span className="absolute size-3 rounded-full bg-danger text-white flex items-center justify-center text-[7px] font-bold top-1 right-[4px]">
                                                    {
                                                        userNotifications.filter(
                                                            (n) => !n.is_read
                                                        ).length
                                                    }
                                                </span>
                                            )}
                                        </>
                                    </ActionButton>
                                </PopoverTrigger>
                                <PopoverContent
                                    className={
                                        (styles.notfi_pp, "!z-[10] !w-[350px]")
                                    }
                                    align="end"
                                >
                                    <NotificationBox
                                        data={userNotifications}
                                        type="not_reminder"
                                        isError={false}
                                        loading={false}
                                    />
                                </PopoverContent>
                            </Popover>
                        )}

                        <div className="h-5 w-[1.5px] bg-stroke mr-2 ml-2" />

                        <div>
                            <Dropdown modal={false}>
                                <DropdownTrigger asChild>
                                    <ActionButton
                                        variant="can-hover"
                                        size="auto"
                                        iconRight={
                                            <svg
                                                viewBox="0 0 24 24"
                                                width="24"
                                                height="24"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="size-4 shrink-0"
                                            >
                                                <path d="M12 3h7a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-7m0-18H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7m0-18v18" />
                                            </svg>
                                        }
                                        actionTitle="Select theme"
                                    >
                                        <span className="hidden md:inline-block">
                                            Theme
                                        </span>
                                    </ActionButton>
                                </DropdownTrigger>
                                <DropdownContent
                                    width={120}
                                    onCloseAutoFocus={(event) =>
                                        event.preventDefault()
                                    }
                                >
                                    <DropdownItem
                                        onClick={() =>
                                            handleThemeChange("light")
                                        }
                                        isSelected={currentTheme === "light"}
                                    >
                                        Light
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() =>
                                            handleThemeChange("h-green")
                                        }
                                        isSelected={currentTheme === "h-green"}
                                    >
                                        H-green
                                    </DropdownItem>
                                </DropdownContent>
                            </Dropdown>
                        </div>

                        <div className="ml-1">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <ActionButton
                                        variant="can-hover"
                                        size="auto"
                                        className="hidden md:flex gap-x-1"
                                        actionTitle="View profile"
                                    >
                                        <div className="size-5 rounded-full [border:1.5px_solid_var(--border-theme-switch)] flex items-center justify-center text-[9px] text-text font-semibold shrink-0">
                                            {userInitials(
                                                removeEnumUnderscore(
                                                    loggedInUserData.first_name
                                                ),
                                                removeEnumUnderscore(
                                                    loggedInUserData.last_name
                                                )
                                            )}
                                        </div>

                                        <span className="font-medium text-base">
                                            {loggedInUserData.first_name}{" "}
                                            {loggedInUserData.last_name}
                                        </span>
                                    </ActionButton>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="mt-2">
                                    <div className="flex items-center gap-x-2">
                                        <div className="size-8 rounded-full [border:1.5px_solid_var(--border-theme-switch)] flex items-center justify-center text-base text-text font-semibold shadow-sm">
                                            {userInitials(
                                                removeEnumUnderscore(
                                                    loggedInUserData.first_name
                                                ),
                                                removeEnumUnderscore(
                                                    loggedInUserData.last_name
                                                )
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-y-0.5">
                                            <span className="font-medium text-base">
                                                {loggedInUserData.first_name}{" "}
                                                {loggedInUserData.last_name}
                                            </span>
                                            <span className="font-normal  text-textSec leading-[16px] capitalize text-caption">
                                                {convertArrayToString(
                                                    practice?.roles || [],
                                                    ", "
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="pt-[--spacing-items] mt-[--spacing-items] [border-top:1px_solid_var(--color-stroke)]">
                                        <Button
                                            className="text-danger cursor-pointer"
                                            size="auto"
                                            variant="normal"
                                            type="button"
                                            onClick={logOutUser}
                                            iconLeft={
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    width="24"
                                                    height="24"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="size-4"
                                                >
                                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                                    <polyline points="16 17 21 12 16 7" />
                                                    <line
                                                        x1="21"
                                                        y1="12"
                                                        x2="9"
                                                        y2="12"
                                                    />
                                                </svg>
                                            }
                                        >
                                            Logout
                                        </Button>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>
                )}
            </nav>
            {isStaging && (
                <div className="py-[2px] border-t border-t-danger border-b border-b-danger bg-[#fbc7c67f] text-danger font-semibold text-caption uppercase text-center">
                    Viewing test site
                </div>
            )}
        </div>
    );
}
